// src/pages/Orders/OrderForm/orderFormHooks.enhanced.js - UPDATED WITH CORRECT FULFILLMENT STRUCTURE
import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  notifySuccess,
  notifyError,
  notifyInfo,
} from "../../../utils/notification";
import {
  createDirectOrderThunk,
  getOrderDraftThunk,
  finalizeOrderThunk,
  createOrderFromCartThunk,
  createOrderFromSessionThunk,
} from "../../../store/thunks/orderThunks";

export const useOrderForm = (mode = "add", orderId = null) => {
  const dispatch = useDispatch();
  const location = useLocation();

  // Redux state
  const { loading } = useSelector((state) => state.orders);
  const { branches, selectedBranch } = useSelector((state) => state.branch);
  const { user } = useSelector((state) => state.auth);
  const adminInfo = {
    adminCode: user?.adminCode || user?._id,
    adminName: user?.name,
    adminId: user?._id,
  };

  // Determine order source from location state
  const locationState = location.state || {};
  const orderSource =
    locationState.mode ||
    (locationState.cartData ? "from-cart" : null) ||
    (locationState.sessionData ? "from-session" : null) ||
    "direct";

  const cartData = locationState.cartData;
  const sessionData = locationState.sessionData;
  const isFromCart = orderSource === "from-cart" || orderSource === "cart";
  const isFromSession =
    orderSource === "from-session" || orderSource === "session";

  // Form state - UPDATED STRUCTURE
  const [formData, setFormData] = useState({
    source: orderSource,
    userId: null,
    guestCustomer: {
      name: "",
      phone: "",
      email: "",
    },
    sessionId: null,
    sessionCode: null,
    cartId: null,
    items: [],
    exchangeItems: [],
    orderAdjustments: [], // Order-level adjustments

    // CORRECTED: fulfillment structure matching backend
    fulfillment: {
      type: "PICKUP",
      location: {
        branchCode: selectedBranch,
        storeCode: "",
        address: {
          line1: "",
          line2: "",
          city: "",
          state: "",
          pincode: "",
          country: "India",
        },
      },
      pickup: {
        scheduledDate: "",
        scheduledTimeSlot: {
          start: "10:00",
          end: "12:00",
        },
        pickupNotes: "",
      },
      delivery: {
        scheduledDate: "",
        scheduledTimeSlot: {
          start: "10:00",
          end: "12:00",
        },
        deliveryNotes: "",
        deliveryCharges: 0,
      },
      specialInstructions: "",
    },

    payment: {
      method: "CASH",
      status: "PENDING",
    },
    notes: "",
    adminNotes: "",
  });

  const [errors, setErrors] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showUserSelection, setShowUserSelection] = useState(false);
  const [showProductSelection, setShowProductSelection] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [pricingSummary, setPricingSummary] = useState(null);

  // Source-specific state
  const [cartPricing, setCartPricing] = useState(null);
  const [sessionPricing, setSessionPricing] = useState(null);
  const [cartAdjustments, setCartAdjustments] = useState([]);
  const [sessionAdjustments, setSessionAdjustments] = useState([]);

  // Initialize based on source
  useEffect(() => {
    if (mode === "add") {
      if (isFromCart && cartData) {
        initializeFromCart(cartData);
      } else if (isFromSession && sessionData) {
        initializeFromSession(sessionData);
      }
    }
  }, [mode, isFromCart, isFromSession, cartData, sessionData]);

  // Initialize from Cart
  const initializeFromCart = (data) => {
    console.log("Initializing from cart:", data);

    // Extract cart adjustments (coupons)
    const adjustments = (data.appliedCoupons || []).map((coupon) => ({
      type: typeof coupon === "string" ? "COUPON" : coupon.type,
      code: typeof coupon === "string" ? coupon : coupon.code,
      discount: typeof coupon === "object" ? coupon.discount : 0,
    }));
    setCartAdjustments(adjustments);

    // Map exchange items from cart
    const exchangeItems = (data.exchangeItems || []).map((item) => ({
      exchangeItemId: item._id || item.exchangeItemId,
      itemType: item.itemType,
      description: item.description,
      grossWeight: item.grossWeight,
      netWeight: item.netWeight,
      stoneWeight: item.stoneWeight || 0,
      metalDetails: item.metalDetails,
      conditionGrade: item.condition || item.conditionGrade,
      images: item.images || [],
      evaluatedValue: item.valuation?.estimatedValue || 0,
      deductions: Object.values(item.valuation?.deductions || {}).reduce(
        (sum, val) => sum + val,
        0
      ),
      finalValue: item.valuation?.finalValue || 0,
    }));

    setFormData({
      ...formData,
      source: "cart",
      cartId: data.cartId,
      userId: data.userId,
      sessionId: data.sessionId,
      items: data.items || [],
      exchangeItems,
      fulfillment: {
        ...formData.fulfillment,
        location: {
          ...formData.fulfillment.location,
          branchCode: data.branchCode || selectedBranch,
        },
      },
    });

    if (data.userId) {
      setSelectedUser({ _id: data.userId });
    }

    notifySuccess("Cart data loaded successfully");
  };

  // Initialize from Session
  const initializeFromSession = (data) => {
    console.log("Initializing from session:", data);

    // Extract session pricing
    const lockedPrices = data.session?.priceLock?.lockedPrices;
    if (lockedPrices) {
      setSessionPricing(lockedPrices.summary);
      setPricingSummary(lockedPrices.summary);
    }

    setSelectedSession(data.session);

    // Extract session adjustments (active only)
    const adjustments = (
      data.session?.priceAdjustments ||
      data.priceAdjustments ||
      []
    ).filter((adj) => adj.status === "ACTIVE" && adj.approved !== false);
    setSessionAdjustments(adjustments);

    // Map exchange items from session (only accepted ones)
    const acceptedExchangeItems = (data.session?.exchangeItems || [])
      .filter((item) => item.customerAccepted)
      .map((item) => ({
        exchangeItemId: item.exchangeItemId,
        itemType: item.itemType,
        description: item.description,
        grossWeight: item.grossWeight,
        netWeight: item.netWeight,
        stoneWeight: item.stoneWeight || 0,
        metalDetails: item.metalDetails,
        conditionGrade: item.conditionGrade,
        images: item.images || [],
        evaluatedValue: item.systemAppraisal?.grossValue || 0,
        deductions: item.systemAppraisal?.totalDeductions || 0,
        finalValue: item.agreedValue || item.systemAppraisal?.finalValue || 0,
        systemAppraisal: item.systemAppraisal,
        manualAdjustment: item.manualAdjustment,
      }));

    // Initialize form data
    setFormData({
      ...formData,
      source: "session",
      userId: data.userId || data.session?.userId?._id,
      sessionId: data.sessionId || data.session?._id,
      sessionCode: data.sessionCode || data.session?.sessionCode,
      cartId: data.cartId,
      guestCustomer: data.guestCustomer ||
        data.session?.guestCustomer || {
          name: "",
          phone: "",
          email: "",
        },
      items: data.items || [],
      exchangeItems: acceptedExchangeItems,
      fulfillment: {
        ...formData.fulfillment,
        location: {
          ...formData.fulfillment.location,
          branchCode: data.session?.branchCode || selectedBranch,
          storeCode: data.session?.storeCode || "",
        },
      },
      adminNotes:
        `Created from session: ${
          data.sessionCode || data.session?.sessionCode
        }\n` +
        `Session Type: ${data.session?.sessionType || "Unknown"}\n` +
        `Primary Admin: ${data.session?.primaryAdmin?.adminName || "N/A"}`,
    });

    if (data.session?.userId) {
      setSelectedUser(data.session.userId);
    }

    notifySuccess("Session data loaded successfully");
  };

  // Load draft order in edit mode
  useEffect(() => {
    if (mode === "edit" && orderId) {
      loadDraftOrder();
    }
  }, [mode, orderId]);

  const loadDraftOrder = async () => {
    try {
      const result = await dispatch(getOrderDraftThunk(orderId)).unwrap();
      const order = result.data.order;

      setFormData({
        ...formData,
        source: order.source || "direct",
        userId: order.userId?._id || null,
        guestCustomer: order.guestCustomer || formData.guestCustomer,
        sessionId: order.sessionReference?.sessionId || null,
        sessionCode: order.sessionReference?.sessionCode || null,
        cartId: order.sourceReference?.cartId || null,
        items: order.items || [],
        exchangeItems: order.exchangeItems || [],
        orderAdjustments:
          order.priceAdjustments?.filter((adj) => adj.scope === "ORDER") || [],
        fulfillment: order.fulfillment || formData.fulfillment,
        payment: order.payment || formData.payment,
        notes: order.notes || "",
        adminNotes: order.adminNotes || "",
      });

      if (order.userId) {
        setSelectedUser(order.userId);
      }

      if (order.orderSummary) {
        setPricingSummary(order.orderSummary);
      }

      // Load existing adjustments
      if (order.priceAdjustments) {
        const cartAdj = order.priceAdjustments.filter(
          (adj) => adj.scope === "CART" || adj.adjustmentType === "COUPON"
        );
        const sessionAdj = order.priceAdjustments.filter(
          (adj) => adj.scope === "SESSION"
        );
        setCartAdjustments(cartAdj);
        setSessionAdjustments(sessionAdj);
      }
    } catch (error) {
      console.error("Failed to load draft order:", error);
      notifyError(error || "Failed to load order");
    }
  };

  useEffect(() => {
    // Don't auto-calculate on initial mount
    if (formData.items.length === 0) {
      return;
    }

    // Debounce to prevent excessive calculations
    const timeoutId = setTimeout(() => {
      handleRecalculatePricing();
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [
    formData.items,
    formData.exchangeItems,
    formData.orderAdjustments,
    formData.fulfillment?.location?.branchCode,
    // Don't include handleRecalculatePricing in deps to avoid infinite loop
  ]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const keys = name.split(".");
      setFormData((prev) => {
        const newData = { ...prev };
        let current = newData;

        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }

        current[keys[keys.length - 1]] = value;
        return newData;
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // User selection handlers
  const handleSelectUser = (user) => {
    if (!user) return;

    setSelectedUser(user);
    setFormData((prev) => ({
      ...prev,
      userId: user._id,
      guestCustomer: {
        name: "",
        phone: "",
        email: "",
      },
    }));
    setShowUserSelection(false);

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.customer;
      delete newErrors["guestCustomer.name"];
      delete newErrors["guestCustomer.phone"];
      delete newErrors["guestCustomer.email"];
      return newErrors;
    });

    notifySuccess("Customer selected");
  };

  const handleDeselectUser = () => {
    setSelectedUser(null);
    setFormData((prev) => ({
      ...prev,
      userId: null,
      guestCustomer: {
        name: "",
        phone: "",
        email: "",
      },
    }));

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.customer;
      return newErrors;
    });
  };

  // Session selection handlers
  const handleSelectSession = (session) => {
    if (!session) return;

    console.log("Session selected:", session);

    setSelectedSession(session);

    const lockedPrices = session.priceLock?.lockedPrices;
    if (lockedPrices) {
      setSessionPricing(lockedPrices.summary);
      setPricingSummary(lockedPrices.summary);
    }

    const adjustments = (session.priceAdjustments || []).filter(
      (adj) => adj.status === "ACTIVE" && adj.approved !== false
    );
    setSessionAdjustments(adjustments);

    const acceptedExchangeItems = (session.exchangeItems || [])
      .filter((item) => item.customerAccepted)
      .map((item) => ({
        exchangeItemId: item.exchangeItemId,
        itemType: item.itemType,
        description: item.description,
        grossWeight: item.grossWeight,
        netWeight: item.netWeight,
        stoneWeight: item.stoneWeight || 0,
        metalDetails: item.metalDetails,
        conditionGrade: item.conditionGrade,
        images: item.images || [],
        evaluatedValue: item.systemAppraisal?.grossValue || 0,
        deductions: item.systemAppraisal?.totalDeductions || 0,
        finalValue: item.agreedValue || item.systemAppraisal?.finalValue || 0,
      }));

    setFormData((prev) => ({
      ...prev,
      source: "session",
      sessionId: session._id,
      sessionCode: session.sessionCode,
      userId: session.userId?._id || prev.userId,
      guestCustomer: session.guestCustomer || prev.guestCustomer,
      exchangeItems: acceptedExchangeItems,
      fulfillment: {
        ...prev.fulfillment,
        location: {
          ...prev.fulfillment.location,
          branchCode:
            session.branchCode || prev.fulfillment.location.branchCode,
        },
      },
      adminNotes:
        prev.adminNotes ||
        `Linked to session: ${session.sessionCode}\n` +
          `Session Type: ${session.sessionType}\n` +
          `Primary Admin: ${session.primaryAdmin?.adminName || "N/A"}`,
    }));

    if (session.userId) {
      setSelectedUser(session.userId);
    }

    notifySuccess("Session linked to order");
  };

  const handleDeselectSession = () => {
    if (
      !window.confirm(
        "Remove session link? Exchange items and pricing will be cleared."
      )
    ) {
      return;
    }

    setSelectedSession(null);
    setSessionPricing(null);
    setSessionAdjustments([]);
    setFormData((prev) => ({
      ...prev,
      source: "direct",
      sessionId: null,
      sessionCode: null,
      exchangeItems: [],
    }));

    notifyInfo("Session unlinked from order");
  };

  // Product management handlers
  const handleAddProducts = (productsOrProduct) => {
    if (!productsOrProduct) return;

    const products = Array.isArray(productsOrProduct)
      ? productsOrProduct
      : [productsOrProduct];

    if (products.length === 0) {
      notifyError("No products selected");
      return;
    }

    let addedCount = 0;
    let updatedCount = 0;

    const newItems = [...formData.items];

    products.forEach((product) => {
      if (!product || typeof product !== "object") return;

      const productId = product._id || product.productId;
      if (!productId) return;

      const existingIndex = newItems.findIndex(
        (item) => (item.productId || item.product?._id) === productId
      );

      if (existingIndex > -1) {
        newItems[existingIndex].quantity += 1;
        updatedCount++;
      } else {
        const newItem = {
          productId: productId,
          product: product,
          productSnapshot: {
            _id: productId,
            name: product.name,
            productId: product.productId,
            type: product.type,
            subType: product.subType,
            grossWeight: product.grossWeight,
            netWeight: product.netWeight,
            purity: product.purity,
            metalColor: product.metalColor,
            hallmark: product.hallmark,
            media: product.media,
            stock: product.stock,
          },
          quantity: 1,
          pricingSnapshot: product.pricing || null,
        };

        newItems.push(newItem);
        addedCount++;
      }
    });

    setFormData((prev) => ({
      ...prev,
      items: newItems,
    }));

    setShowProductSelection(false);

    if (errors.items) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.items;
        return newErrors;
      });
    }

    if (addedCount > 0 || updatedCount > 0) {
      const messages = [];
      if (addedCount > 0) messages.push(`${addedCount} product(s) added`);
      if (updatedCount > 0) messages.push(`${updatedCount} quantity updated`);
      notifySuccess(messages.join(", "));
    }

    handleRecalculatePricing();
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      handleRemoveItem(productId);
      return;
    }

    const newItems = formData.items.map((item) =>
      (item.productId || item.product?._id) === productId
        ? { ...item, quantity: newQuantity }
        : item
    );

    setFormData((prev) => ({ ...prev, items: newItems }));
    handleRecalculatePricing();
  };

  const handleRemoveItem = (productId) => {
    const newItems = formData.items.filter(
      (item) => (item.productId || item.product?._id) !== productId
    );
    setFormData((prev) => ({ ...prev, items: newItems }));
    handleRecalculatePricing();
  };

  // Exchange item handlers
  const handleAddExchangeItem = (exchangeItem) => {
    setFormData((prev) => ({
      ...prev,
      exchangeItems: [...prev.exchangeItems, exchangeItem],
    }));
    handleRecalculatePricing();
  };

  const handleUpdateExchangeItem = (index, updatedItem) => {
    const newExchangeItems = [...formData.exchangeItems];
    newExchangeItems[index] = updatedItem;
    setFormData((prev) => ({ ...prev, exchangeItems: newExchangeItems }));
    handleRecalculatePricing();
  };

  const handleRemoveExchangeItem = (index) => {
    const newExchangeItems = formData.exchangeItems.filter(
      (_, i) => i !== index
    );
    setFormData((prev) => ({ ...prev, exchangeItems: newExchangeItems }));
    handleRecalculatePricing();
  };

  // Order adjustment handlers
  const handleAddOrderAdjustment = (adjustment) => {
    setFormData((prev) => ({
      ...prev,
      orderAdjustments: [...prev.orderAdjustments, adjustment],
    }));
    handleRecalculatePricing();
    notifySuccess("Order adjustment added");
  };

  const handleUpdateOrderAdjustment = (index, updatedAdjustment) => {
    const newAdjustments = [...formData.orderAdjustments];
    newAdjustments[index] = updatedAdjustment;
    setFormData((prev) => ({ ...prev, orderAdjustments: newAdjustments }));
    handleRecalculatePricing();
    notifySuccess("Order adjustment updated");
  };

  const handleRemoveOrderAdjustment = (index) => {
    const newAdjustments = formData.orderAdjustments.filter(
      (_, i) => i !== index
    );
    setFormData((prev) => ({ ...prev, orderAdjustments: newAdjustments }));
    handleRecalculatePricing();
    notifySuccess("Order adjustment removed");
  };

  // Enhanced pricing calculation with all adjustment types
  const handleRecalculatePricing = useCallback(async () => {
    // Use session pricing if locked
    if (selectedSession?.priceLock?.isLocked && sessionPricing) {
      console.log("Using locked pricing from session");
      applyOrderAdjustmentsToLockedPricing();
      return;
    }

    if (formData.items.length === 0) {
      setPricingSummary(null);
      return;
    }

    setCalculating(true);

    try {
      const selectedBranchCode =
        formData.fulfillment?.location?.branchCode || selectedBranch;

      if (!selectedBranchCode) {
        console.warn("No branch selected for pricing calculation");
        setCalculating(false);
        return;
      }

      console.log("Calculating pricing for branch:", selectedBranchCode);

      // ==========================================
      // STEP 1: Calculate Base Item Prices
      // ==========================================
      let processedItems = [];
      let totalMetalValue = 0;
      let totalMakingCharges = 0;
      let totalWastageCharges = 0;
      let totalStoneCharges = 0;
      let totalGST = 0;
      let totalItemDiscount = 0;

      formData.items.forEach((item, idx) => {
        if (!item.pricingSnapshot) {
          console.warn(`Item ${idx} missing pricing snapshot`);
          return;
        }

        // Get branch pricing
        const branchPricing =
          item.pricingSnapshot.branches?.[selectedBranchCode];

        if (!branchPricing) {
          console.warn(
            `No pricing for branch ${selectedBranchCode} in item ${idx}`
          );
          return;
        }

        const breakdown = branchPricing.breakdown || {};
        const quantity = item.quantity || 1;

        // Base calculations
        const itemMetalValue = (breakdown.metalValue || 0) * quantity;
        const itemMakingCharges = (breakdown.makingCharges || 0) * quantity;
        const itemWastageCharges = (breakdown.wastageCharges || 0) * quantity;
        const itemStoneCharges = (breakdown.stoneCharges || 0) * quantity;
        const itemSubtotal =
          itemMetalValue +
          itemMakingCharges +
          itemWastageCharges +
          itemStoneCharges;

        // ==========================================
        // STEP 2: Apply ITEM-scoped adjustments
        // ==========================================
        const itemAdjustments = formData.orderAdjustments.filter(
          (adj) =>
            adj.status === "ACTIVE" &&
            adj.scope === "ITEM" &&
            adj.targetProductId === (item.productId || item.product?._id)
        );

        let itemFinalPrice = itemSubtotal;
        let itemAdjustmentTotal = 0;
        let appliedAdjustments = [];

        itemAdjustments.forEach((adj) => {
          const result = applyItemAdjustment(itemFinalPrice, adj);
          itemAdjustmentTotal += result.discount;
          itemFinalPrice = result.finalPrice;
          appliedAdjustments.push({
            type: adj.adjustmentType,
            description: result.adjustmentApplied,
            discount: result.discount,
          });
        });

        // Calculate GST on adjusted price
        const itemGST = (itemFinalPrice * (breakdown.gst?.rate || 3)) / 100;

        // Accumulate totals
        totalMetalValue += itemMetalValue;
        totalMakingCharges += itemMakingCharges;
        totalWastageCharges += itemWastageCharges;
        totalStoneCharges += itemStoneCharges;
        totalItemDiscount += itemAdjustmentTotal;
        totalGST += itemGST;

        // Store processed item
        processedItems.push({
          productId: item.productId || item.product?._id,
          productName: item.product?.name || item.productSnapshot?.name,
          quantity: quantity,
          sku: item.product?.sku || item.productSnapshot?.sku,
          basePrice: itemSubtotal,
          adjustments: appliedAdjustments,
          adjustmentTotal: itemAdjustmentTotal,
          priceAfterAdjustments: itemFinalPrice,
          gst: itemGST,
          finalPrice: itemFinalPrice + itemGST,
          pricingSnapshot: branchPricing,
        });
      });

      // ==========================================
      // STEP 3: Calculate Subtotal
      // ==========================================
      let subtotal =
        totalMetalValue +
        totalMakingCharges +
        totalWastageCharges +
        totalStoneCharges -
        totalItemDiscount;

      // ==========================================
      // STEP 4: Apply CART-scoped adjustments
      // ==========================================
      const cartAdjustments = formData.orderAdjustments.filter(
        (adj) => adj.status === "ACTIVE" && adj.scope === "CART"
      );

      let totalCartDiscount = 0;
      let appliedCartAdjustments = [];

      cartAdjustments.forEach((adj) => {
        const result = applyCartAdjustment(subtotal, adj);
        totalCartDiscount += result.discount;
        appliedCartAdjustments.push({
          type: adj.adjustmentType,
          description: result.adjustmentApplied,
          discount: result.discount,
        });
      });

      // Adjust subtotal after cart discounts
      subtotal = subtotal - totalCartDiscount;

      // ==========================================
      // STEP 5: Add existing cart/session adjustments
      // ==========================================
      let existingAdjustmentsTotal = 0;

      // Cart adjustments (from cart)
      if (isFromCart && cartAdjustments.length > 0) {
        cartAdjustments.forEach((adj) => {
          if (adj.discount) {
            existingAdjustmentsTotal += adj.discount;
          }
        });
      }

      // Session adjustments (from consultation)
      if (isFromSession && sessionAdjustments.length > 0) {
        sessionAdjustments.forEach((adj) => {
          if (adj.adjustment?.type === "FIXED_AMOUNT") {
            existingAdjustmentsTotal += adj.adjustment.value;
          } else if (adj.adjustment?.type === "PERCENTAGE") {
            existingAdjustmentsTotal += (subtotal * adj.adjustment.value) / 100;
          }
        });
      }

      // ==========================================
      // STEP 6: Calculate Grand Total
      // ==========================================
      const totalAdjustments =
        totalItemDiscount + totalCartDiscount + existingAdjustmentsTotal;
      const grandTotal = subtotal + totalGST;

      // ==========================================
      // STEP 7: Process Exchange Items
      // ==========================================
      let processedExchangeItems = [];
      let totalExchangeValue = 0;

      formData.exchangeItems.forEach((item) => {
        if (item.customerAccepted !== true) {
          // Not accepted - don't deduct
          processedExchangeItems.push({
            ...item,
            adjustments: [],
            bonusTotal: 0,
            originalValue: item.finalValue || item.agreedValue,
            finalValue: item.finalValue || item.agreedValue,
          });
          return;
        }

        // Apply EXCHANGE_ITEM-scoped adjustments
        const exchangeAdjustments = formData.orderAdjustments.filter(
          (adj) => adj.status === "ACTIVE" && adj.scope === "EXCHANGE_ITEM"
        );

        let exchangeValue = item.finalValue || item.agreedValue || 0;
        let exchangeBonusTotal = 0;
        let appliedExchangeAdjustments = [];

        exchangeAdjustments.forEach((adj) => {
          const result = applyExchangeAdjustment(exchangeValue, adj);
          exchangeBonusTotal += result.bonus;
          exchangeValue = result.finalValue;
          appliedExchangeAdjustments.push({
            type: adj.adjustmentType,
            description: result.adjustmentApplied,
            bonus: result.bonus,
          });
        });

        totalExchangeValue += exchangeValue;

        processedExchangeItems.push({
          exchangeItemId: item.exchangeItemId,
          description: item.description,
          netWeight: item.netWeight,
          metalDetails: item.metalDetails,
          originalValue: item.finalValue || item.agreedValue,
          adjustments: appliedExchangeAdjustments,
          bonusTotal: exchangeBonusTotal,
          finalValue: exchangeValue,
          customerAccepted: item.customerAccepted,
          status: item.status,
        });
      });

      // ==========================================
      // STEP 8: Calculate Final Amount
      // ==========================================
      const netAmount = grandTotal - totalExchangeValue;
      const amountDue = Math.max(0, netAmount);
      const refundDue = Math.max(0, -netAmount);

      // ==========================================
      // STEP 9: Build Summary
      // ==========================================
      const summary = {
        totalItems: processedItems.reduce(
          (sum, item) => sum + item.quantity,
          0
        ),
        uniqueItems: processedItems.length,

        // Base values
        totalMetalValue,
        totalMakingCharges,
        totalWastageCharges,
        totalStoneCharges,

        // Adjustments breakdown
        itemAdjustmentsTotal: totalItemDiscount,
        cartAdjustmentsTotal: totalCartDiscount,
        existingAdjustmentsTotal: existingAdjustmentsTotal,
        totalAdjustments: totalAdjustments,

        // Subtotal and taxes
        subtotal,
        totalGST,
        gstRate: 3,

        // Grand total
        grandTotal,

        // Exchange
        totalExchangeValue,

        // Final amounts
        amountDue,
        refundDue,
        netPayable: amountDue,
        netRefund: refundDue,

        // Transaction type
        transactionType:
          refundDue > 0 ? "REFUND" : amountDue > 0 ? "PAYMENT" : "BALANCED",

        // Detailed items with adjustments
        items: processedItems,

        // Exchange items with bonuses
        exchangeItems: processedExchangeItems,

        // Applied adjustments for display
        appliedItemAdjustments: processedItems
          .filter((item) => item.adjustments.length > 0)
          .map((item) => ({
            productName: item.productName,
            adjustments: item.adjustments,
          })),

        appliedCartAdjustments: appliedCartAdjustments,

        appliedExchangeAdjustments: processedExchangeItems
          .filter((item) => item.adjustments.length > 0)
          .map((item) => ({
            description: item.description,
            adjustments: item.adjustments,
          })),

        calculatedAt: new Date().toISOString(),
      };

      setPricingSummary(summary);

      console.log("✅ Pricing calculated with adjustments:", summary);
    } catch (error) {
      console.error("❌ Pricing calculation failed:", error);
      notifyError("Failed to calculate pricing: " + error.message);
    } finally {
      setCalculating(false);
    }
  }, [
    formData.items,
    formData.exchangeItems,
    formData.orderAdjustments, // 🔥 ADD THIS DEPENDENCY
    formData.fulfillment?.location?.branchCode,
    selectedSession,
    sessionPricing,
    isFromCart,
    isFromSession,
    cartAdjustments,
    sessionAdjustments,
  ]);

  const applyItemAdjustment = (itemPrice, adjustment) => {
    if (adjustment.adjustment.type === "PERCENTAGE") {
      const discount = (itemPrice * adjustment.adjustment.value) / 100;
      return {
        originalPrice: itemPrice,
        discount: discount,
        finalPrice: itemPrice - discount,
        adjustmentApplied: `${adjustment.adjustment.value}% off`,
      };
    } else if (adjustment.adjustment.type === "FIXED_AMOUNT") {
      const discount = Math.min(adjustment.adjustment.value, itemPrice); // Can't discount more than price
      return {
        originalPrice: itemPrice,
        discount: discount,
        finalPrice: itemPrice - discount,
        adjustmentApplied: `₹${discount} off`,
      };
    } else if (adjustment.adjustment.type === "OVERRIDE_PRICE") {
      return {
        originalPrice: itemPrice,
        discount: itemPrice - adjustment.adjustment.overridePrice,
        finalPrice: adjustment.adjustment.overridePrice,
        adjustmentApplied: `Price set to ₹${adjustment.adjustment.overridePrice}`,
      };
    }

    return {
      originalPrice: itemPrice,
      discount: 0,
      finalPrice: itemPrice,
      adjustmentApplied: null,
    };
  };

  // ==========================================
  // 2. Apply Cart-Scoped Adjustments
  // ==========================================
  const applyCartAdjustment = (subtotal, adjustment) => {
    if (adjustment.adjustment.type === "PERCENTAGE") {
      const discount = (subtotal * adjustment.adjustment.value) / 100;
      return {
        discount: discount,
        adjustmentApplied: `${adjustment.adjustment.value}% off cart`,
      };
    } else if (adjustment.adjustment.type === "FIXED_AMOUNT") {
      const discount = Math.min(adjustment.adjustment.value, subtotal);
      return {
        discount: discount,
        adjustmentApplied: `₹${discount} off cart`,
      };
    }

    return {
      discount: 0,
      adjustmentApplied: null,
    };
  };

  // ==========================================
  // 3. Apply Exchange Item Adjustments
  // ==========================================
  const applyExchangeAdjustment = (exchangeValue, adjustment) => {
    if (adjustment.adjustment.type === "PERCENTAGE") {
      // Bonus percentage on exchange
      const bonus = (exchangeValue * adjustment.adjustment.value) / 100;
      return {
        originalValue: exchangeValue,
        bonus: bonus,
        finalValue: exchangeValue + bonus,
        adjustmentApplied: `+${adjustment.adjustment.value}% exchange bonus`,
      };
    } else if (adjustment.adjustment.type === "FIXED_AMOUNT") {
      return {
        originalValue: exchangeValue,
        bonus: adjustment.adjustment.value,
        finalValue: exchangeValue + adjustment.adjustment.value,
        adjustmentApplied: `+₹${adjustment.adjustment.value} exchange bonus`,
      };
    }

    return {
      originalValue: exchangeValue,
      bonus: 0,
      finalValue: exchangeValue,
      adjustmentApplied: null,
    };
  };

  const applyOrderAdjustmentsToLockedPricing = () => {
    if (!sessionPricing) return;

    let orderAdjustmentsTotal = 0;
    formData.orderAdjustments.forEach((adj) => {
      if (adj.status !== "ACTIVE") return;

      if (adj.adjustment.type === "FIXED_AMOUNT") {
        orderAdjustmentsTotal += adj.adjustment.value;
      } else if (adj.adjustment.type === "PERCENTAGE") {
        orderAdjustmentsTotal +=
          (sessionPricing.subtotal * adj.adjustment.value) / 100;
      }
    });

    const adjustedSummary = {
      ...sessionPricing,
      orderAdjustments: orderAdjustmentsTotal,
      grandTotal: sessionPricing.grandTotal - orderAdjustmentsTotal,
      netPayable:
        sessionPricing.netPayable ||
        sessionPricing.grandTotal -
          orderAdjustmentsTotal -
          (sessionPricing.totalExchangeValue || 0),
    };

    setPricingSummary(adjustedSummary);
  };

  // Validation - UPDATED
  const validateForm = () => {
    const newErrors = {};

    // Customer validation
    if (!formData.userId && !formData.guestCustomer?.name) {
      newErrors.customer = "Please select a customer or enter guest details";
    }

    if (!formData.userId && formData.guestCustomer?.name) {
      if (formData.guestCustomer.name.length < 3) {
        newErrors["guestCustomer.name"] = "Name must be at least 3 characters";
      }

      if (!formData.guestCustomer.phone) {
        newErrors["guestCustomer.phone"] = "Phone number is required";
      } else if (!/^[6-9]\d{9}$/.test(formData.guestCustomer.phone)) {
        newErrors["guestCustomer.phone"] = "Invalid phone number";
      }

      if (
        formData.guestCustomer.email &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.guestCustomer.email)
      ) {
        newErrors["guestCustomer.email"] = "Invalid email format";
      }
    }

    // Items validation
    if (!formData.items || formData.items.length === 0) {
      newErrors.items = "Please add at least one item";
    }

    // Fulfillment validation - UPDATED paths
    if (!formData.fulfillment?.location?.branchCode) {
      newErrors["fulfillment.location.branchCode"] = "Branch is required";
    }

    // Address validation for delivery
    if (formData.fulfillment?.type === "DELIVERY") {
      if (!formData.fulfillment.location?.address?.line1) {
        newErrors["fulfillment.location.address.line1"] =
          "Address line 1 is required";
      }
      if (!formData.fulfillment.location?.address?.city) {
        newErrors["fulfillment.location.address.city"] = "City is required";
      }
      if (!formData.fulfillment.location?.address?.state) {
        newErrors["fulfillment.location.address.state"] = "State is required";
      }
      if (!formData.fulfillment.location?.address?.pincode) {
        newErrors["fulfillment.location.address.pincode"] =
          "Pincode is required";
      } else if (
        !/^\d{6}$/.test(formData.fulfillment.location.address.pincode)
      ) {
        newErrors["fulfillment.location.address.pincode"] = "Invalid pincode";
      }
    }

    // Payment validation
    if (!formData.payment?.method) {
      newErrors["payment.method"] = "Payment method is required";
    }

    // Order adjustments validation
    const pendingApprovals = formData.orderAdjustments.filter(
      (adj) => adj.requiresApproval && adj.status === "PENDING"
    );
    if (pendingApprovals.length > 0) {
      notifyInfo(
        `${pendingApprovals.length} adjustment(s) require approval before finalizing`
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission with source-specific handling
  const handleSubmit = async () => {
    if (!validateForm()) {
      throw new Error("Validation failed");
    }

    const orderData = {
      ...formData,
      createdBy: {
        adminId: adminInfo.adminId,
        adminName: adminInfo.adminName,
      },
    };

    // Remove guest customer if user is selected
    if (formData.userId) {
      delete orderData.guestCustomer;
    }

    // Edit mode
    if (mode === "edit" && orderId) {
      const result = await dispatch(finalizeOrderThunk(orderId)).unwrap();
      return result.data;
    }

    // Create from Cart
    if (isFromCart && formData.cartId) {
      const cartOrderData = {
        cartId: formData.cartId,
        userId: formData.userId,
        sessionId: formData.sessionId,
        pricing: cartPricing,
        cartAdjustments: cartAdjustments,
        orderAdjustments: formData.orderAdjustments,
        exchangeItems: formData.exchangeItems,
        fulfillmentLocation: formData.fulfillment, // Backend expects this field name
        payment: formData.payment,
        notes: formData.notes,
        adminNotes: formData.adminNotes,
        createdBy: orderData.createdBy,
      };

      const result = await dispatch(
        createOrderFromCartThunk(cartOrderData)
      ).unwrap();
      return result.data?.order || result.data;
    }

    // Create from Session
    if (isFromSession && formData.sessionId) {
      const sessionOrderData = {
        sessionId: formData.sessionId,
        sessionCode: formData.sessionCode,
        userId: formData.userId,
        cartId: formData.cartId,
        pricing: sessionPricing || pricingSummary,
        sessionAdjustments: sessionAdjustments,
        orderAdjustments: formData.orderAdjustments,
        priceAdjustments: sessionAdjustments.concat(formData.orderAdjustments),
        exchangeItems: formData.exchangeItems,
        fulfillmentLocation: formData.fulfillment, // Backend expects this field name
        payment: formData.payment,
        notes: formData.notes,
        adminNotes: formData.adminNotes,
        sessionMetadata: sessionData?.sessionMetadata,
        createdBy: orderData.createdBy,
      };

      const result = await dispatch(
        createOrderFromSessionThunk({
          sessionId: formData.sessionId,
          orderData: sessionOrderData,
        })
      ).unwrap();
      return result.data?.order || result.data;
    }

    // Direct order creation
    const result = await dispatch(createDirectOrderThunk(orderData)).unwrap();
    return result.data.order;
  };

  const handleSaveDraft = async () => {
    if (!formData.items || formData.items.length === 0) {
      throw new Error("Please add at least one item");
    }

    const orderData = {
      ...formData,
      status: "DRAFT",
      createdBy: {
        adminId: adminInfo.adminId,
        adminName: adminInfo.adminName,
      },
    };

    if (formData.userId) {
      delete orderData.guestCustomer;
    }

    // Route to appropriate creation method based on source
    if (isFromCart && formData.cartId) {
      const result = await dispatch(
        createOrderFromCartThunk(orderData)
      ).unwrap();
      return result.data.order;
    } else if (isFromSession && formData.sessionId) {
      const result = await dispatch(
        createOrderFromSessionThunk({
          sessionId: formData.sessionId,
          orderData,
        })
      ).unwrap();
      return result.data.order;
    } else {
      const result = await dispatch(createDirectOrderThunk(orderData)).unwrap();
      return result.data.order;
    }
  };

  return {
    // Form state
    formData,
    errors,
    selectedUser,
    selectedSession,
    calculating,
    pricingSummary,

    // Source info
    orderSource,
    isFromCart,
    isFromSession,
    cartData,
    sessionData,
    cartPricing,
    sessionPricing,
    cartAdjustments,
    sessionAdjustments,

    // UI state
    showUserSelection,
    setShowUserSelection,
    showProductSelection,
    setShowProductSelection,
    loading,

    // Form handlers
    handleChange,
    handleSelectUser,
    handleDeselectUser,
    handleSelectSession,
    handleDeselectSession,
    handleAddProducts,
    handleUpdateQuantity,
    handleRemoveItem,
    handleAddExchangeItem,
    handleUpdateExchangeItem,
    handleRemoveExchangeItem,
    handleAddOrderAdjustment,
    handleUpdateOrderAdjustment,
    handleRemoveOrderAdjustment,
    handleRecalculatePricing,
    handleSubmit,
    handleSaveDraft,

    // Validation
    validateForm,

    // Data
    branches,
    adminInfo,
  };
};

export default useOrderForm;
